import axios from 'axios';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * SHU (Sisa Hasil Usaha) Service
 * Handles all API calls for cooperative profit distribution
 */

// =====================================================
// SHU CALCULATIONS
// =====================================================

/**
 * Get all SHU calculations with pagination
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise} Response with calculations and pagination meta
 */
export const getSHUCalculations = async (page = 1, limit = 10) => {
  const response = await axios.get(`${API_URL}/shu/calculations`, {
    params: { page, limit }
  });
  return response.data;
};

/**
 * Get single SHU calculation by ID
 * @param {number} id - Calculation ID
 * @returns {Promise} SHU calculation with distributions
 */
export const getSHUCalculation = async (id) => {
  const response = await axios.get(`${API_URL}/shu/calculations/${id}`);
  return response.data;
};

/**
 * Create new SHU calculation
 * @param {Object} data - Calculation data
 * @param {number} data.fiscal_year - Fiscal year (e.g., 2025)
 * @param {string} data.period_start_date - Start date (YYYY-MM-DD)
 * @param {string} data.period_end_date - End date (YYYY-MM-DD)
 * @param {string} [data.notes] - Optional notes
 * @returns {Promise} Created calculation
 */
export const createSHUCalculation = async (data) => {
  const response = await axios.post(`${API_URL}/shu/calculations`, data);
  return response.data;
};

/**
 * Calculate SHU amounts and distribute to members
 * @param {number} id - Calculation ID
 * @param {Object} data - Calculation parameters
 * @param {number} [data.tax_percentage] - Tax percentage (default 0)
 * @returns {Promise} Calculated SHU with member distributions
 */
export const calculateSHU = async (id, data = {}) => {
  const response = await axios.post(`${API_URL}/shu/calculations/${id}/calculate`, data);
  return response.data;
};

/**
 * Approve SHU calculation
 * @param {number} id - Calculation ID
 * @param {Object} data - Approval data
 * @param {string} [data.notes] - Approval notes
 * @returns {Promise} Approved calculation
 */
export const approveSHU = async (id, data = {}) => {
  const response = await axios.post(`${API_URL}/shu/calculations/${id}/approve`, data);
  return response.data;
};

/**
 * Distribute SHU to members
 * @param {number} id - Calculation ID
 * @param {Object} data - Distribution data
 * @param {string} data.payment_method - Payment method (cash, bank_transfer, added_to_savings)
 * @param {string} [data.payment_date] - Payment date (YYYY-MM-DD)
 * @param {string} [data.notes] - Distribution notes
 * @returns {Promise} Success message
 */
export const distributeSHU = async (id, data) => {
  const response = await axios.post(`${API_URL}/shu/calculations/${id}/distribute`, data);
  return response.data;
};

/**
 * Post SHU calculation to ledger
 * @param {number} id - Calculation ID
 * @returns {Promise} Success message
 */
export const postSHU = async (id) => {
  const response = await axios.post(`${API_URL}/shu/calculations/${id}/post`);
  return response.data;
};

// =====================================================
// SHU DISTRIBUTIONS
// =====================================================

/**
 * Update distribution payment status
 * @param {number} id - Distribution ID
 * @param {Object} data - Status update data
 * @param {string} data.distribution_status - Status (pending, ready, paid, cancelled)
 * @param {string} [data.payment_method] - Payment method
 * @param {string} [data.payment_date] - Payment date (YYYY-MM-DD)
 * @param {string} [data.payment_reference] - Payment reference number
 * @returns {Promise} Success message
 */
export const updateDistributionStatus = async (id, data) => {
  const response = await axios.put(`${API_URL}/shu/distributions/${id}/status`, data);
  return response.data;
};

// =====================================================
// SHU REPORTS
// =====================================================

/**
 * Get SHU history for a specific member
 * @param {number} memberId - Member ID
 * @returns {Promise} Array of member's SHU distributions
 */
export const getMemberSHUHistory = async (memberId) => {
  const response = await axios.get(`${API_URL}/shu/members/${memberId}/history`);
  return response.data;
};

/**
 * Get SHU summary report for a fiscal year
 * @param {number} fiscalYear - Fiscal year (e.g., 2025)
 * @returns {Promise} Summary report with statistics
 */
export const getSHUSummary = async (fiscalYear) => {
  const response = await axios.get(`${API_URL}/shu/summary`, {
    params: { fiscal_year: fiscalYear }
  });
  return response.data;
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Format currency to Indonesian Rupiah
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

/**
 * Get status badge color for SHU calculation
 * @param {string} status - Status (draft, calculated, approved, distributed, posted)
 * @returns {string} Tailwind CSS class for badge color
 */
export const getCalculationStatusColor = (status) => {
  const colors = {
    draft: 'bg-gray-200 text-gray-800',
    calculated: 'bg-blue-200 text-blue-800',
    approved: 'bg-green-200 text-green-800',
    distributed: 'bg-purple-200 text-purple-800',
    posted: 'bg-indigo-200 text-indigo-800'
  };
  return colors[status] || 'bg-gray-200 text-gray-800';
};

/**
 * Get status badge color for distribution
 * @param {string} status - Status (pending, ready, paid, cancelled)
 * @returns {string} Tailwind CSS class for badge color
 */
export const getDistributionStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-200 text-yellow-800',
    ready: 'bg-blue-200 text-blue-800',
    paid: 'bg-green-200 text-green-800',
    cancelled: 'bg-red-200 text-red-800'
  };
  return colors[status] || 'bg-gray-200 text-gray-800';
};

/**
 * Get status label in Indonesian
 * @param {string} status - Status in English
 * @param {string} type - Type (calculation or distribution)
 * @returns {string} Status label in Indonesian
 */
export const getStatusLabel = (status, type = 'calculation') => {
  if (type === 'calculation') {
    const labels = {
      draft: 'Draft',
      calculated: 'Terhitung',
      approved: 'Disetujui',
      distributed: 'Didistribusikan',
      posted: 'Dibukukan'
    };
    return labels[status] || status;
  } else {
    const labels = {
      pending: 'Menunggu',
      ready: 'Siap',
      paid: 'Dibayar',
      cancelled: 'Dibatalkan'
    };
    return labels[status] || status;
  }
};

/**
 * Get payment method label in Indonesian
 * @param {string} method - Payment method
 * @returns {string} Method label in Indonesian
 */
export const getPaymentMethodLabel = (method) => {
  const labels = {
    cash: 'Tunai',
    bank_transfer: 'Transfer Bank',
    added_to_savings: 'Ditambahkan ke Simpanan'
  };
  return labels[method] || method;
};

/**
 * Validate fiscal year
 * @param {number} year - Year to validate
 * @returns {boolean} True if valid
 */
export const isValidFiscalYear = (year) => {
  const currentYear = new Date().getFullYear();
  return year >= 2000 && year <= currentYear;
};

/**
 * Get fiscal year options for dropdown
 * @param {number} [startYear=2020] - Start year
 * @returns {Array} Array of year options
 */
export const getFiscalYearOptions = (startYear = 2020) => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= startYear; year--) {
    years.push({ value: year, label: year.toString() });
  }
  return years;
};

export default {
  // Calculations
  getSHUCalculations,
  getSHUCalculation,
  createSHUCalculation,
  calculateSHU,
  approveSHU,
  distributeSHU,
  postSHU,
  
  // Distributions
  updateDistributionStatus,
  
  // Reports
  getMemberSHUHistory,
  getSHUSummary,
  
  // Helpers
  formatCurrency,
  getCalculationStatusColor,
  getDistributionStatusColor,
  getStatusLabel,
  getPaymentMethodLabel,
  isValidFiscalYear,
  getFiscalYearOptions
};
